import * as React from 'react';
import {transparentize} from 'polished';
import styled, {css} from 'styled-components';
import {StyledContainer} from 'containers/Container';
import IconBox from './IconBox';
import {LessonObject} from 'api/lesson/types';
import {WebinarObject} from "api/webinar/types";
import {ChapterContentObject, ChapterContentType} from 'api/course';
import ScheduleItemContent, {Container as ScheduleItemContainer, Icon,} from './ScheduleItemContent';
import {
  chaptersGetLesson, chaptersGetWebinar,
  getScheduleItemActiveProp,
  renderScheduleItemIcon,
  validateAndGetLessons,
  validateAndGetWebinars,
} from '../helpers';
import {transition} from 'helpers/css';

export enum ScheduleItemStatus {
  Complete='complete',
  Closed='closed',
  Opened='opened',
  isRest='isRestDay'
}

export interface ScheduleItemProps {
  day: string;
  title?: string;
  status: ScheduleItemStatus;
  content?: ChapterContentObject[];
  opened?: boolean;
  closed?: boolean;
}

const Content = styled.div`
  display: grid;
  gap: 10px;
  height: 0;
  overflow: hidden;
  
  ${transition()}
`;

const ContentItemsWrapper = styled.div`
  display: grid;
  gap: 10px;
  
  @media (max-width: 640px) {
    gap: 6px;
    
    &[data-content-type="lessons"], 
    &[data-content-type="webinars"], 
    &[data-content-type="tasks"] {
      ${ScheduleItemContainer} {
        &:not(:first-child) {
          ${Icon} {
            display: none;
          }
        }
      }
    }
  }
`;

const OpenButton = styled.div`
  position: absolute;
  top: 0;
  right: 15px;
  bottom: 0;
  width: 30px;
  background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 5L5 1L9 5' stroke='white' stroke-opacity='0.3' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
  background-repeat: no-repeat;
  background-position: center;
  transform: rotate(0);
  ${transition()}
`;

const Container = styled(StyledContainer)<Pick<ScheduleItemProps, 'opened'>>`
  position: relative;
  width: 100%;
  
  &::before {
    content: '';
    display: block;
    width: 2px;
    position: absolute;
    left: 43px;
    top: 40px;
    bottom: -14px;
    background: #454548;
    z-index: 1;

    @media (max-width: 640px) {
      display: none;
    }
  }
  
  &:last-child {
    &::before {
      display: ${({opened}) => opened ? 'block' : 'none'};

      @media (max-width: 640px) {
        display: none;
      }
    }
  }
  
  ${Content} {
    ${({opened}) => {
    return opened && css`
        height: max-content;
        padding: 20px 0;
      `;
  }}}
  
  ${OpenButton} {
    ${({opened}) => {
    return opened && css`
        transform: rotate(180deg);
      `;
  }}}
`;

const Main = styled.div`
  position: relative;
  padding: 10px 14px;
  display: flex;
  background: ${({theme}) => theme.variables.accentBackgroundColor};
  border: 1px solid ${({theme}) => `
    ${transparentize(.95, theme.variables.color)};
  `};
  border-radius: 5px;
  margin-bottom: -1px;
  cursor: pointer;
  
  height: 60px;
  box-sizing: border-box;
`;

const Info = styled.div`
  display: grid;
  gap: 6px;
  margin-left: 14px;
`;

const Date = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: ${({theme}) => `
    ${transparentize(.6, theme.variables.color)};
  `};
`;

/**
 * This component renders course schedule item
 * @param {ScheduleItemProps} props
 * @return {React.ReactElement}
 * @constructor
 */
export default function ScheduleItem(props: ScheduleItemProps)
  : React.ReactElement {
  const [dataLessons, setDataLessons] = React.useState<LessonObject[]>([]);
  const [dataWebinars, setDataWebinars] = React.useState<WebinarObject[]>([]);
  // const [dataTasks, setDataTasks] = React.useState<TaskObject[]>([]);

  const [isOpened, setOpened] = React.useState(
      Boolean(props.opened && !props.closed),
  );

  React.useEffect(() => {
    const content = props.content;

    if (content) {
      /** fetch and set to data Lessons */
      Promise.all(content.map(chaptersGetLesson))
          .then((response) => validateAndGetLessons(response))
          .then((items) => setDataLessons(items))
          .catch(console.error);

      /** fetch and set to data Webinars */
      Promise.all(content.map(chaptersGetWebinar))
          .then((response) => validateAndGetWebinars(response))
          .then((items) => setDataWebinars(items))
          .catch(console.error);

      // /** fetch and set to data Tasks */
      // Promise.all(content.map(chaptersGetTask))
      //     .then((response) => validateAndGetTasks(response))
      //     .then((items) => setDataTasks(items))
      //     .catch(console.error);
    }

  }, [props.content]);


  /**
   * the function toggle isOpened state
   * @constructor
   */
  function toggleOpened() {
    if (!props.closed) {
      setOpened((prev) => !prev);
    }
  }

  /**
   * the function renders course schedule item status icon
   * @return {React.ReactElement}
   * @constructor
   */
  function renderIconBox() {
    const iconProps = {
      size: 26,
      shadow: true,
      active: getScheduleItemActiveProp(props.status),
      icon: renderScheduleItemIcon(props.status),
    };

    return (
      <IconBox
        {...iconProps}
      />
    );
  }

  /**
   * the function renders course schedule item status icon
   * @return {React.ReactElement}
   * @constructor
   */
  function renderContentLessons(lessons: LessonObject[]) {
    return (
        <ContentItemsWrapper data-content-type="lessons">
          {lessons.map((lesson) => (
            <ScheduleItemContent
              key={lesson.id}
              content={lesson}
              type={ChapterContentType.Lesson}
            />
          ))}
        </ContentItemsWrapper>
    );
  }

  /**
   * the function renders course schedule item status icon
   * @return {React.ReactElement}
   * @constructor
   */
  function renderContentWebinars(webinars: WebinarObject[]) {
    return (
        <ContentItemsWrapper data-content-type="webinars">
          {webinars.map((webinar) => (
            <ScheduleItemContent
              key={webinar.id}
              content={webinar}
              type={ChapterContentType.Webinar}
            />
          ))}
        </ContentItemsWrapper>
    );
  }

  function checkContentExist() {
    const haveLessons = dataLessons.length > 0;
    const haveWebinars = dataWebinars.length > 0;

    return haveLessons || haveWebinars;
  }

  return (
    <Container opened={isOpened && checkContentExist()}>
      <Main onClick={toggleOpened}>
        {renderIconBox()}
        <Info>
          <Date>{props.day}</Date>
          {props.title}
        </Info>
        {checkContentExist() && !props.closed && <OpenButton />}
      </Main>
      <Content>
        {renderContentLessons(dataLessons)}
        {renderContentWebinars(dataWebinars)}
      </Content>
    </Container>
  );
}
